import React, { useMemo, useState } from 'react';
import './PromptAnalystForm.scss';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import type { CreatePromptAnalysisRequest } from '@brand24-original/llm-types';
import { MonitorPrompt } from '../../../shared/models/monitor/MonitorPrompt';

// Te same granice co `PromptAnalysisController` — tu tylko po to, żeby nie wysyłać czegoś, co odbije.
const PROMPT_MIN_LENGTH = 3;
const PROMPT_MAX_LENGTH = 500;
/** Starter chips under the field, like ChatGPT's suggestions — the project's newest prompts. */
const SUGGESTIONS = 6;

const keyOf = (text: string) => text.trim().toLowerCase();

interface PromptAnalystFormProps {
  prompts: MonitorPrompt[];
  /** The backend guards the quota anyway; this only disables the button. */
  isQuotaExhausted: boolean;
  isBusy: boolean;
  error: string | null;
  onSubmit: (body: CreatePromptAnalysisRequest) => void;
}

/**
 * The new-analysis screen: one centred field, like an empty ChatGPT conversation. Text that is exactly
 * a project prompt (typed or picked from the suggestions) goes out as `monitorId`, so the review uses
 * its measured answers; anything else is a new prompt.
 */
const PromptAnalystForm: React.FC<PromptAnalystFormProps> = ({
  prompts,
  isQuotaExhausted,
  isBusy,
  error,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');

  const monitorByText = useMemo(() => new Map(prompts.map((p) => [keyOf(p.prompt), p.id])), [prompts]);
  const suggestions = useMemo(
    () =>
      [...prompts]
        .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
        .slice(0, SUGGESTIONS),
    [prompts]
  );
  const trimmed = prompt.trim();
  const canSubmit =
    !isBusy &&
    !isQuotaExhausted &&
    trimmed.length >= PROMPT_MIN_LENGTH &&
    trimmed.length <= PROMPT_MAX_LENGTH;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const monitorId = monitorByText.get(keyOf(trimmed));
    onSubmit(monitorId != null ? { monitorId } : { prompt: trimmed });
  };

  return (
    <form className="prompt-analyst-form" onSubmit={handleSubmit}>
      <h2 className="prompt-analyst-form__title">{t('promptAnalyst.composerTitle')}</h2>

      <div className="prompt-analyst-form__row">
        <span className="prompt-analyst-form__field">
          <InputText
            className="prompt-analyst-form__input"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={t('promptAnalyst.promptPlaceholder')}
            maxLength={PROMPT_MAX_LENGTH}
            disabled={isBusy}
            autoFocus
          />
        </span>
        <Button type="submit" className="btn btn--sm prompt-analyst-form__submit" disabled={!canSubmit}>
          <span className={`${isBusy ? 'ico--loader' : 'ico--zap'} ico--xs ico--white`} />
          {isBusy ? t('promptAnalyst.analyzing') : t('promptAnalyst.analyzeBtn')}
        </Button>
      </div>

      {suggestions.length > 0 && (
        <div className="prompt-analyst-form__suggestions">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              type="button"
              className={`prompt-analyst-form__suggestion${
                keyOf(suggestion.prompt) === keyOf(prompt)
                  ? ' prompt-analyst-form__suggestion--active'
                  : ''
              }`}
              onClick={() => setPrompt(suggestion.prompt)}
              disabled={isBusy}
            >
              {suggestion.prompt}
            </button>
          ))}
        </div>
      )}

      {error && <span className="form__error">{error}</span>}
    </form>
  );
};

export default PromptAnalystForm;
